import { createFileRoute } from "@tanstack/react-router";
import { optionParam, textParam } from "@/utils/searchParams";

/**
 * The two halves of "work that is mine and is not done" (Jira NAV-111).
 *
 * They were two navigation entries answering the same question — "My Evaluation
 * Steps" and "Waiting on you" — so somebody named on both landed with two
 * entries and no way to tell which was which until they opened one. They are
 * tabs now. `steps` is the default because it is the older and much commoner
 * half, and because a default that moved with the counts would take a reader to
 * the other tab the moment they finished their last step.
 */
const QUEUE_TABS = ["steps", "decisions"] as const;
export type QueueTab = (typeof QUEUE_TABS)[number];

/** The tab a bare address lands on. Named rather than written inline because the
 *  navigation entry has to know it in order not to write it (see
 *  `endUserDestinations`), and a second copy of the answer would drift. */
export const DEFAULT_QUEUE_TAB: QueueTab = "steps";

// The tab and the steps queue's filter live in the URL, so leaving and coming
// back keeps what the reader was looking at (NAV-45). The default tab and the
// default filter are simply never written — an address that names one anyway
// still reads correctly.
//
// The three are where a step stands for the reader (Jira NAV-258): their turn,
// still to come, done. An address from before that names one of the old
// three, which `LEGACY_STEP_FILTERS` reads as its nearest successor.
const STEP_FILTERS = ["now", "upcoming", "done"] as const;
const LEGACY_STEP_FILTERS: Record<string, StepFilter | undefined> = {
  pending: "now",
  completed: "done",
  all: undefined,
};

export type StepFilter = (typeof STEP_FILTERS)[number];

/** What the steps tab narrows to before anybody touches the control — named for
 *  the reason {@link DEFAULT_QUEUE_TAB} is: the page has to know it in order not
 *  to write it, and a second copy of the answer drifts. */
export const DEFAULT_STEP_FILTER: StepFilter = "now";

export type WaitingOnYouSearch = {
  tab?: QueueTab;
  filter?: StepFilter;
  /** One request to show open and unfiltered on the steps tab, whatever
   *  `filter` says — the target of a notification that points at this queue
   *  (PRD D7/D10: a step announcement, or the answer to a question the
   *  responsible asked). Without it the notice can land on a page that shows
   *  nothing: cards are collapsed by default, and the default `now` filter
   *  drops the request entirely once the responsible's own step is done — which
   *  is the normal state of somebody who asked, finished, and is now being
   *  answered. */
  request?: string;
};

/**
 * Shared with the three moved addresses that forward here — `/end-user/
 * approvals`, `/end-user/evaluation-steps` and `/end-user/decisions` — which
 * validate the same params in order to carry them across (see `movedRoutes.ts`).
 * Each of them names the tab it meant rather than leaning on the default, so a
 * link sent in an email years ago cannot be re-pointed by a later change of
 * mind about which tab opens first.
 */
export function waitingOnYouSearch(
  search: Record<string, unknown>,
): WaitingOnYouSearch {
  return {
    tab: optionParam(QUEUE_TABS, search.tab),
    filter:
      optionParam(STEP_FILTERS, search.filter) ??
      (typeof search.filter === "string"
        ? LEGACY_STEP_FILTERS[search.filter]
        : undefined),
    request: textParam(search.request),
  };
}

export const Route = createFileRoute("/end-user/waiting-on-you")({
  validateSearch: waitingOnYouSearch,
});
